import { Box, Button, HStack, Input, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminGetIngredients } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminIngredient, IAdminIngredientList } from "../../../types";

export default function Ingredients() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface IIngredientForm {
        keyword: string;
    }

    const { register, watch, handleSubmit, getValues, formState: {errors, isValid}, setError, setValue } = useForm<IIngredientForm>({
        mode: "onChange",
    });

    const [keyword, setKeyword] = useState<string | null>(null);

    const onSubmit = ({ keyword }: IIngredientForm) => {
        setPage(1);
        setKeyword(keyword);
    }

    /* query */

    const { data: ingredientListData, isLoading: isIngredientListDataLoading } = useQuery<IAdminIngredientList>({
        queryKey: ['adminGetIngredients', page, size, keyword],
        queryFn: () => adminGetIngredients({ page, size, keyword }),
        enabled: true,
    });

    const totalPages = useMemo(() => ingredientListData?.totalPages ?? 0, [ingredientListData]);
    const currentPage = useMemo(() => ingredientListData?.currentPage ?? 0, [ingredientListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* log columns */

    const ingredientColumns = [
        { key: "nameKor", label: "한글명" },
        { key: "nameEng", label: "영문명" },
        { key: "nameStd", label: "표준 이름" },
    ]

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>성분</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{ingredientListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>
                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("keyword", {required:false})}
                        type="text" 
                        placeholder="Search" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* table */}

                {!isIngredientListDataLoading ? (
                    <TableContainer marginTop={10}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    {ingredientColumns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {ingredientListData?.ingredients.map((ingredient, index) => (
                                    <Tr key={ingredient.id}>
                                        {ingredientColumns.map((column, idx) => (
                                            <Td key={idx}>
                                                {column.key === "index" ? String(index + 1) : String(ingredient[column.key as keyof IAdminIngredient] ?? "-")}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {/* 이전 페이지 버튼 */}
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {/* 첫 페이지와 '...' */}
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {/* 페이지 버튼들 */}
                    {pages.map((page) => (
                        <Button
                            key={page}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(page)}
                            backgroundColor={page === currentPage ? colors.blue : ""}
                            color={page === currentPage ? colors.white : ""}
                        >
                            {page}
                        </Button>
                    ))}
            
                    {/* 마지막 페이지와 '...' */}
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {/* 다음 페이지 버튼 */}
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}